import { Injectable } from '@angular/core';
import * as TabOrderConstants from '../constants/claims-ui-constants';

@Injectable({
  providedIn: 'root',
})
export class TabOrderService {
  private keydownHandler: ((event: KeyboardEvent) => void) | null = null;

  public getTabOrder(modalType: TabOrderConstants.ModalType = 'assertions') {
    return modalType === 'claims' ? TabOrderConstants.CLAIMS_TAB_ORDER : TabOrderConstants.TAB_ORDER;
  }

  public attachKeydownListener(elRef: any, form: any, modalType: 'assertions' | 'claims' = 'assertions'): void {
    this.detachKeydownListener();
    this.keydownHandler = (event: KeyboardEvent) => {
      if (event.key !== 'Tab') return;
      const host = elRef.nativeElement as HTMLElement;
      if (!host?.contains(document.activeElement)) return;

      const isViewMode = form.get('projectCode')?.disabled;
      const tabbableFields = this.getTabOrder(modalType)
        .map(([tabName, sel]) => {
          let element = host.querySelector<HTMLElement>(sel);
          if (isViewMode && tabName && form.get(tabName)?.disabled) return null;
          if (sel.startsWith('emma-generic-search')) {
            const generic = element?.closest('emma-generic-search');
            if (generic) {
              element =
                (generic.querySelector('.dropdown-display') as HTMLElement) ||
                (generic.querySelector('input') as HTMLElement);
              if (!element) return null;
            }
          }

          return { tabName, el: element };
        })
        .filter((f): f is { tabName: string | null; el: HTMLElement } => !!f && !!f.el && f.el.offsetWidth > 0);
      if (!tabbableFields.length) return;

      event.preventDefault();
      event.stopPropagation();

      const active = document.activeElement as HTMLElement;
      const idx = tabbableFields.findIndex(f => f.el === active || f.el.contains(active) || active.closest?.('ng-select')?.contains(f.el));
      const next = idx === -1
        ? (event.shiftKey ? tabbableFields.length - 1 : 0)
        : (idx + (event.shiftKey ? -1 : 1) + tabbableFields.length) % tabbableFields.length;

      const { el, tabName: fieldName } = tabbableFields[next];
      const control = fieldName ? form.get(fieldName) : null;
      const wasDisabled = control?.disabled;

      if (wasDisabled && control) control.disable({ emitEvent: false });
      if ((el instanceof HTMLInputElement || el instanceof HTMLTextAreaElement) && el.disabled) {
        el.disabled = false;
        el.readOnly = true;
      }
      el.setAttribute('tabindex', '0');

      setTimeout(() => {
        el.focus();
        if (wasDisabled && control) {
          el.addEventListener('blur', () => {
            control.disable({ emitEvent: false });
            if (el instanceof HTMLInputElement || el instanceof HTMLTextAreaElement) el.readOnly = false;
          }, { once: true });
        }
      });
    };
    document.addEventListener('keydown', this.keydownHandler, { capture: true });
  }

  public detachKeydownListener(): void {
    if (this.keydownHandler) {
      document.removeEventListener('keydown', this.keydownHandler, true);
      this.keydownHandler = null;
    }
  }
}
